import {
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  InputProps,
  Box,
} from '@chakra-ui/react';
import { forwardRef, useState } from 'react';
import { useAppStore } from '~/store';

interface FloatingLabelInputProps extends Omit<InputProps, 'size'> {
  label: string;
  error?: string;
  isInvalid?: boolean;
  isRequired?: boolean;
  useFloatingLabel?: boolean;
}

export const FloatingLabelInput = forwardRef<
  HTMLInputElement,
  FloatingLabelInputProps
>(
  (
    { label, error, isInvalid, isRequired, useFloatingLabel, ...props },
    ref
  ) => {
    const [isFocused, setIsFocused] = useState(false);
    const [hasValue, setHasValue] = useState(
      !!props.value || !!props.defaultValue
    );
    const isMobile = useAppStore((state) => state.isMobile);

    // Only apply floating label on mobile
    const showFloatingLabel = useFloatingLabel || isMobile;

    // Determine if label should be shown (either focused or has value)
    const showLabel = showFloatingLabel && (isFocused || hasValue);

    return (
      <FormControl isInvalid={isInvalid} isRequired={isRequired}>
        {/* Standard label for desktop */}
        {!showFloatingLabel && <FormLabel>{label}</FormLabel>}

        {/* Floating label container */}
        <Box position="relative">
          {/* Floating label for mobile */}
          {showFloatingLabel && (
            <FormLabel
              position="absolute"
              left={3}
              top="50%"
              pointerEvents="none"
              transform={
                showLabel 
                  ? 'translateY(-32px) scale(1)' 
                  : 'translateY(-50%)'
              }
              transformOrigin="left center"
              color={showLabel ? 'blackAlpha.900' : 'blackAlpha.700'}
              fontSize={showLabel ? 'xs' : 'md'}
              opacity={showLabel ? 1 : isFocused ? 0 : 1}
              transition="all 0.2s ease-out"
              zIndex={2}
              bg={showLabel ? 'white' : 'transparent'}
              px={showLabel ? 1 : 1}
              mb={0}
            >
              {label}
            </FormLabel>
          )}

          <Input
            ref={ref}
            {...props}
            onFocus={(e) => {
              setIsFocused(true);
              props.onFocus?.(e);
            }}
            onBlur={(e) => {
              setIsFocused(false);
              setHasValue(!!e.target.value);
              props.onBlur?.(e);
            }}
            onChange={(e) => {
              setHasValue(!!e.target.value);
              props.onChange?.(e);
            }}
            placeholder={
              showFloatingLabel && isFocused
                ? ''
                : showFloatingLabel
                  ? ''
                  : props.placeholder
            }
            defaultValue={props.defaultValue}
            height="48px"
            py="0"
            px="3"
            backgroundColor="blackAlpha.100"
            borderColor="transparent"
            borderRadius="4px"
            fontWeight="medium"
            letterSpacing="tight"
            sx={{
              '&::placeholder': {
                position: 'relative',
                top: '0',
              }
            }}
            _hover={{
              borderColor: 'transparent',
              backgroundColor: 'blackAlpha.100',
            }}
            _focus={{
              borderColor: 'brand.500',
              backgroundColor: 'white',
              boxShadow: 'none',
            }}
          />
        </Box>

        {error && (
          <FormErrorMessage fontSize="xs" mt="2">
            {error}
          </FormErrorMessage>
        )}
      </FormControl>
    );
  }
);

FloatingLabelInput.displayName = 'FloatingLabelInput';
